import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Clock, Copy, Clipboard, Pencil, Check } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';
import { formatTime12Hour } from '@/utils/calendarHelpers';
import { DAY_NAMES } from '@/utils/constants';

interface TemplateData {
  id: string;
  day_of_week: number;
  windows: { start: string; end: string }[];
  auto_assign_user_id: string | null;
}

interface AssignableUser {
  id: string;
  first_name: string;
  last_name: string | null;
  emoji: string | null;
  role: string;
}

export function ScheduleTab({ compact }: { compact?: boolean } = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editingDay, setEditingDay] = useState<number | null>(null);
  const [newWindowStart, setNewWindowStart] = useState('09:00');
  const [newWindowEnd, setNewWindowEnd] = useState('17:00');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedWindows, setCopiedWindows] = useState<{ start: string; end: string }[] | null>(null);
  const [dragOverDay, setDragOverDay] = useState<number | null>(null);
  const [updatingAutoAssign, setUpdatingAutoAssign] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');

  // Fetch assignable users (caretakers + family)
  const { data: assignableUsers = [] } = useQuery<AssignableUser[]>({
    queryKey: ['assignable-users'],
    queryFn: async () => {
      const res = await api.get('/users');
      const data = res.data;
      return (data.users || data || []).filter((u: any) => u.role === 'caretaker' || u.role === 'family');
    },
    staleTime: 1000 * 60,
  });

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['availability-templates'],
    queryFn: async () => {
      const res = await api.get('/availability/templates');
      return res.data.templates || [];
    },
    staleTime: 1000 * 5,
    refetchInterval: 5000,
  });

  // Group templates by day of week
  const templatesByDay = useMemo(() => {
    const grouped: Record<number, TemplateData[]> = {};
    for (let i = 0; i < 7; i++) {
      grouped[i] = [];
    }
    (templates as TemplateData[]).forEach((t) => {
      grouped[t.day_of_week].push(t);
    });
    return grouped;
  }, [templates]);

  // Create template
  const handleCreateTemplate = async (dayOfWeek: number) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post('/availability/templates', {
        day_of_week: dayOfWeek,
        windows: [{ start: newWindowStart, end: newWindowEnd }],
      });
      queryClient.invalidateQueries({ queryKey: ['availability-templates'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      setEditingDay(null);
      setNewWindowStart('09:00');
      setNewWindowEnd('17:00');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.delete(`/availability/templates/${templateId}`);
      queryClient.invalidateQueries({ queryKey: ['availability-templates'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy windows from a day
  const handleCopyDay = (dayIndex: number) => {
    const dayTemplates = templatesByDay[dayIndex] || [];
    const windows = dayTemplates.flatMap(t => t.windows);
    if (windows.length > 0) {
      setCopiedWindows(windows);
    }
  };

  // Paste windows to a day
  const handlePasteToDay = async (dayIndex: number) => {
    if (!copiedWindows || copiedWindows.length === 0) return;
    setIsSubmitting(true);
    setError(null);
    try {
      for (const window of copiedWindows) {
        await api.post('/availability/templates', {
          day_of_week: dayIndex,
          windows: [window],
        });
      }
      queryClient.invalidateQueries({ queryKey: ['availability-templates'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle drop on a day
  const handleDropOnDay = async (targetDay: number, sourceDay: number, window: { start: string; end: string }) => {
    if (targetDay === sourceDay) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post('/availability/templates', {
        day_of_week: targetDay,
        windows: [window],
      });
      queryClient.invalidateQueries({ queryKey: ['availability-templates'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
      setDragOverDay(null);
    }
  };

  // Update auto-assign for a template
  const handleAutoAssignChange = async (templateId: string, userId: string | null) => {
    setUpdatingAutoAssign(templateId);
    try {
      await api.put(`/availability/templates/${templateId}`, {
        auto_assign_user_id: userId,
      });
      queryClient.invalidateQueries({ queryKey: ['availability-templates'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUpdatingAutoAssign(null);
    }
  };

  // Edit template time windows
  const handleEditTemplate = async (templateId: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.put(`/availability/templates/${templateId}`, {
        windows: [{ start: editStart, end: editEnd }],
      });
      queryClient.invalidateQueries({ queryKey: ['availability-templates'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      setEditingTemplateId(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="container-schedule-tab" className={compact ? 'space-y-4' : 'space-y-6'}>
      <div data-testid="container-template-editor" className={`bg-card rounded-xl border border-slate-700 ${compact ? 'p-4' : 'p-6'}`}>
        <div className={compact ? 'mb-4' : 'mb-6'}>
          <h2 className={`font-semibold text-white ${compact ? 'text-base' : 'text-xl'}`}>Weekly Schedule</h2>
          <p className="text-gray-400 text-sm mt-1">
            Set recurring availability for each day of the week. Caretakers can claim time within these windows.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Clipboard indicator */}
        {copiedWindows && copiedWindows.length > 0 && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <Clipboard size={16} />
              <span>Copied: {copiedWindows.map(w => `${formatTime12Hour(w.start)}-${formatTime12Hour(w.end)}`).join(', ')}</span>
            </div>
            <button
              onClick={() => setCopiedWindows(null)}
              data-testid="clickable-schedule-clear-clipboard"
              className="p-1 text-gray-400 hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : (
          <div className="space-y-3">
            {DAY_NAMES.map((dayName, dayIndex) => {
              const dayTemplates = templatesByDay[dayIndex] || [];
              const isEditing = editingDay === dayIndex;
              const hasWindows = dayTemplates.some(t => t.windows.length > 0);

              return (
                <div
                  key={dayIndex}
                  data-testid={`droppable-template-day-${dayIndex}`}
                  className={`bg-slate-800 border rounded-lg p-4 transition-colors ${
                    dragOverDay === dayIndex ? 'border-emerald-500 bg-emerald-500/5' : 'border-slate-700'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverDay(dayIndex);
                  }}
                  onDragLeave={() => setDragOverDay(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    try {
                      const data = JSON.parse(e.dataTransfer.getData('application/json'));
                      handleDropOnDay(dayIndex, data.dayIndex, data.window);
                    } catch {}
                    setDragOverDay(null);
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-medium w-24">{dayName}</span>
                      {dayTemplates.length === 0 && !isEditing && (
                        <span className="text-gray-500 text-sm">No availability set</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Copy button */}
                      {hasWindows && (
                        <button
                          onClick={() => handleCopyDay(dayIndex)}
                          data-testid={`clickable-copy-day-${dayIndex}`}
                          className="p-1.5 text-gray-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                          title="Copy day's schedule"
                        >
                          <Copy size={16} />
                        </button>
                      )}
                      {/* Paste button */}
                      {copiedWindows && copiedWindows.length > 0 && (
                        <button
                          onClick={() => handlePasteToDay(dayIndex)}
                          disabled={isSubmitting}
                          data-testid={`clickable-paste-day-${dayIndex}`}
                          className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded transition-colors disabled:opacity-50"
                          title="Paste schedule here"
                        >
                          <Clipboard size={16} />
                        </button>
                      )}
                      {!isEditing && (
                        <button
                          data-testid={`clickable-add-window-day-${dayIndex}`}
                          onClick={() => setEditingDay(dayIndex)}
                          className="px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                        >
                          + Add Window
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Existing windows - now draggable */}
                  {dayTemplates.map((template) => (
                    <div key={template.id} className="mb-2">
                      {template.windows.map((window, winIdx) => (
                        editingTemplateId === template.id ? (
                          <div
                            key={winIdx}
                            className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg mb-1"
                          >
                            <Clock size={16} className="text-emerald-400 flex-shrink-0" />
                            <input
                              type="time"
                              value={editStart}
                              onChange={(e) => setEditStart(e.target.value)}
                              className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-28"
                            />
                            <span className="text-gray-400 text-sm">-</span>
                            <input
                              type="time"
                              value={editEnd}
                              onChange={(e) => setEditEnd(e.target.value)}
                              className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-28"
                            />
                            <button
                              onClick={() => handleEditTemplate(template.id)}
                              disabled={isSubmitting}
                              className="p-1 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20 rounded transition-colors disabled:opacity-50"
                              title="Save"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => setEditingTemplateId(null)}
                              className="p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-500/20 rounded transition-colors"
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div
                            key={winIdx}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('application/json', JSON.stringify({
                                dayIndex,
                                window,
                                templateId: template.id,
                              }));
                              e.dataTransfer.effectAllowed = 'copy';
                            }}
                            data-testid={`draggable-template-window-${dayIndex}-${window.start.replace(':', '')}-${window.end.replace(':', '')}`}
                            className="flex items-center justify-between p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg mb-1 cursor-grab active:cursor-grabbing"
                          >
                            <div className="flex items-center gap-2">
                              <Clock size={16} className="text-emerald-400" />
                              <span className="text-emerald-400 text-sm">
                                {formatTime12Hour(window.start)} - {formatTime12Hour(window.end)}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTemplateId(template.id);
                                  setEditStart(window.start);
                                  setEditEnd(window.end);
                                }}
                                className="p-0.5 text-gray-400 hover:text-gray-300 hover:bg-gray-500/20 rounded transition-colors"
                                title="Edit time"
                              >
                                <Pencil size={12} />
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={template.auto_assign_user_id || ''}
                                onChange={(e) => handleAutoAssignChange(template.id, e.target.value || null)}
                                disabled={updatingAutoAssign === template.id}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                data-testid={`select-auto-assign-${template.id}`}
                                className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[130px]"
                              >
                                <option value="">Default: none</option>
                                {assignableUsers.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.emoji || ''} {u.first_name}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleDeleteTemplate(template.id)}
                                disabled={isSubmitting}
                                data-testid={`clickable-delete-template-${dayIndex}-${winIdx}`}
                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  ))}

                  {/* Add window form */}
                  {isEditing && (
                    <div className="mt-3 p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-400 mb-1">Start</label>
                          <input
                            type="time"
                            value={newWindowStart}
                            onChange={(e) => setNewWindowStart(e.target.value)}
                            data-testid="input-template-window-start"
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-400 mb-1">End</label>
                          <input
                            type="time"
                            value={newWindowEnd}
                            onChange={(e) => setNewWindowEnd(e.target.value)}
                            data-testid="input-template-window-end"
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          data-testid="clickable-template-save"
                          onClick={() => handleCreateTemplate(dayIndex)}
                          disabled={isSubmitting}
                          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingDay(null);
                            setError(null);
                          }}
                          data-testid="clickable-template-cancel"
                          className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info box - hidden in compact mode */}
      {!compact && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
          <h3 className="text-blue-400 font-medium mb-2">How it works</h3>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• Set weekly recurring availability above (e.g., Mon-Fri 3PM-8PM)</li>
            <li>• These windows repeat every week automatically</li>
            <li>• Drag time windows to copy them to other days</li>
            <li>• Use copy/paste buttons to duplicate schedules</li>
            <li>• Override specific dates by clicking on the Calendar tab</li>
          </ul>
        </div>
      )}
    </div>
  );
}
